import React, { useMemo } from "react";

/**
 * Renders a tropical (Western) natal chart payload inside the chat thread.
 * Expects `payload.western` shaped like compute_western_chart() output:
 *   { big_three, planets, cusps, aspects, element_balance, modality_balance, ... }
 *
 * Visual: a compact dark-violet natal wheel SVG with planet glyphs on the
 * zodiac wheel, plus a clean placements table and element/modality balance
 * bars below it.
 */

const SIGNS = [
  "Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo",
  "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces",
];
const SIGN_GLYPHS = {
  Aries: "♈", Taurus: "♉", Gemini: "♊", Cancer: "♋", Leo: "♌", Virgo: "♍",
  Libra: "♎", Scorpio: "♏", Sagittarius: "♐", Capricorn: "♑", Aquarius: "♒", Pisces: "♓",
};
const ELEMENT_COLOR = {
  Fire: "#F97316", Earth: "#84CC16", Air: "#FACC15", Water: "#38BDF8",
};
const MODALITY_COLOR = {
  Cardinal: "#EF4444", Fixed: "#A855F7", Mutable: "#10B981",
};
const PLANET_ORDER = [
  "Sun", "Moon", "Mercury", "Venus", "Mars", "Jupiter",
  "Saturn", "Uranus", "Neptune", "Pluto", "North Node", "Chiron",
];
const TONE_LABEL = {
  intense: "Intense",
  harmonious: "Harmonious",
  supportive: "Supportive",
  tense: "Tense",
  polarizing: "Polarizing",
};
const TONE_COLOR = {
  intense: "bg-purple-100 text-purple-800 border-purple-300",
  harmonious: "bg-emerald-100 text-emerald-800 border-emerald-300",
  supportive: "bg-sky-100 text-sky-800 border-sky-300",
  tense: "bg-rose-100 text-rose-800 border-rose-300",
  polarizing: "bg-amber-100 text-amber-800 border-amber-300",
};

// Polar → Cartesian on an SVG of width=2*r, centered at (r, r).
// Angle is measured CCW from +x axis (right). Astrology charts traditionally
// place the Ascendant on the LEFT (180° CCW from the +x axis), with the zodiac
// going CCW.
function polar(cx, cy, r, deg) {
  const rad = (deg * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy - r * Math.sin(rad)];
}

// Convert an absolute zodiac longitude (0-360°) to an SVG-friendly angle so
// that the ASC (asc_longitude on the chart) sits on the left horizon.
function lonToAngle(lon, ascLon) {
  // The chart wheel rotates CCW with Aries 0° at a specific position. We
  // place ASC on the LEFT (180°) and let the rest go CCW.
  return ((lon - ascLon + 360) % 360) + 180;
}

const NatalWheel = ({ planets, ascLon, mcLon, size = 280 }) => {
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = size / 2 - 4;
  const rZodiac = rOuter - 18;
  const rPlanet = rZodiac - 28;
  const rInner = rPlanet - 18;

  // 12 zodiac sectors (each 30°). With our angle convention, ASC sits at 180°.
  const sectors = SIGNS.map((sign, idx) => {
    const startLon = idx * 30;
    const startAng = lonToAngle(startLon, ascLon);
    const endAng = lonToAngle(startLon + 30, ascLon);
    const labelLon = startLon + 15;
    const labelAng = lonToAngle(labelLon, ascLon);
    const [lx, ly] = polar(cx, cy, rZodiac + 9, labelAng);
    return { sign, startAng, endAng, lx, ly };
  });

  const planetGlyphMap = {
    Sun: "☉", Moon: "☽", Mercury: "☿", Venus: "♀", Mars: "♂",
    Jupiter: "♃", Saturn: "♄", Uranus: "♅", Neptune: "♆", Pluto: "♇",
    "North Node": "☊", Chiron: "⚷",
  };

  // Group planets per ~5° bucket so overlapping glyphs spread out a little.
  const items = PLANET_ORDER.map((name) => {
    const p = planets[name];
    if (!p) return null;
    return { name, glyph: planetGlyphMap[name] || "•", lon: p.longitude, sign: p.sign };
  }).filter(Boolean);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ background: "radial-gradient(circle at 50% 50%, #2D1A4D 0%, #1A0B2E 60%, #0A0518 100%)" }}
      data-testid="western-natal-wheel"
    >
      {/* zodiac ring */}
      <circle cx={cx} cy={cy} r={rOuter} fill="none" stroke="#5C3A8A" strokeWidth={1} />
      <circle cx={cx} cy={cy} r={rZodiac} fill="none" stroke="#7C4AC5" strokeWidth={1.2} />
      <circle cx={cx} cy={cy} r={rInner} fill="none" stroke="#5C3A8A" strokeWidth={1} />

      {/* 12 zodiac sector dividers */}
      {sectors.map((s, i) => {
        const [x1, y1] = polar(cx, cy, rInner, s.startAng);
        const [x2, y2] = polar(cx, cy, rOuter, s.startAng);
        return (
          <line
            key={`sec-${i}`}
            x1={x1} y1={y1} x2={x2} y2={y2}
            stroke="#5C3A8A"
            strokeWidth={1}
            opacity={0.6}
          />
        );
      })}

      {/* sign glyphs */}
      {sectors.map((s, i) => (
        <text
          key={`sign-${i}`}
          x={s.lx}
          y={s.ly}
          fill="#FFD86B"
          fontSize={size > 240 ? 14 : 12}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{ fontFamily: "system-ui" }}
        >
          {SIGN_GLYPHS[s.sign]}
        </text>
      ))}

      {/* horizon (ASC/DSC) line */}
      <line
        x1={cx - rOuter} y1={cy}
        x2={cx + rOuter} y2={cy}
        stroke="#FFB800"
        strokeWidth={1}
        opacity={0.65}
        strokeDasharray="3,3"
      />
      {/* MC/IC line — vertical because we placed ASC at 180° (left horizontal) */}
      <line
        x1={cx} y1={cy - rOuter}
        x2={cx} y2={cy + rOuter}
        stroke="#FFB800"
        strokeWidth={1}
        opacity={0.4}
        strokeDasharray="2,4"
      />
      <text x={cx - rOuter + 6} y={cy - 4} fill="#FFB800" fontSize={9} style={{ fontFamily: "system-ui" }}>ASC</text>
      <text x={cx + 4} y={cy - rOuter + 12} fill="#FFB800" fontSize={9} style={{ fontFamily: "system-ui" }}>MC</text>

      {/* planet glyphs */}
      {items.map((it, i) => {
        const ang = lonToAngle(it.lon, ascLon);
        // small radial offset based on idx so overlapping planets spread out
        const radial = rPlanet - (i % 2 === 0 ? 0 : 12);
        const [px, py] = polar(cx, cy, radial, ang);
        return (
          <g key={`p-${it.name}`}>
            <circle cx={px} cy={py} r={9} fill="#1A0B2E" stroke="#FFD86B" strokeWidth={0.8} />
            <text
              x={px}
              y={py + 0.5}
              fill="#FFD86B"
              fontSize={11}
              textAnchor="middle"
              dominantBaseline="middle"
              style={{ fontFamily: "system-ui" }}
            >
              {it.glyph}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

const Bar = ({ label, count, max, color }) => (
  <div className="flex items-center gap-2 text-[11px] text-[#3F2D1B]">
    <span className="w-16 font-medium">{label}</span>
    <div className="flex-1 h-1.5 rounded-full bg-purple-100 overflow-hidden">
      <div
        className="h-full rounded-full"
        style={{ width: `${Math.min(100, (count / Math.max(max, 1)) * 100)}%`, background: color }}
      />
    </div>
    <span className="w-4 text-right tabular-nums">{count}</span>
  </div>
);

const WesternChartMessage = ({ payload, text }) => {
  const w = payload?.western;
  const wheelSize = useMemo(() => {
    if (typeof window !== "undefined" && window.innerWidth < 640) return 240;
    return 300;
  }, []);

  if (!w || !w.planets) return null;

  const bt = w.big_three || {};
  const aspects = w.aspects || [];
  const tightAspects = aspects.filter((a) => a.orb <= 4).slice(0, 8);
  const elem = w.element_balance || {};
  const modal = w.modality_balance || {};
  const maxElem = Math.max(elem.Fire || 0, elem.Earth || 0, elem.Air || 0, elem.Water || 0);
  const maxModal = Math.max(modal.Cardinal || 0, modal.Fixed || 0, modal.Mutable || 0);

  return (
    <div
      className="max-w-[95%] sm:max-w-[88%] bg-gradient-to-br from-[#2D1A4D] to-[#1A0B2E] text-white rounded-2xl rounded-bl-sm px-3 sm:px-4 py-3 shadow-sm border border-[#5C3A8A]"
      data-testid="western-chart-card"
    >
      <p className="label-eyebrow !text-[#FFD86B] !text-[10px]">
        Your western (tropical) natal chart · Placidus
      </p>
      <p className="text-xs text-[#E0D4F2] mt-1.5 mb-3 leading-relaxed">{text}</p>

      {/* Big three pills */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        <span className="px-2 py-1 rounded-md bg-[#3F2865] border border-[#FFD86B]/40 text-[11px] text-[#FFD86B] font-medium" data-testid="western-sun">
          ☉ Sun · {bt.sun_sign}
        </span>
        <span className="px-2 py-1 rounded-md bg-[#3F2865] border border-[#FFD86B]/40 text-[11px] text-[#FFD86B] font-medium" data-testid="western-moon">
          ☽ Moon · {bt.moon_sign}
        </span>
        <span className="px-2 py-1 rounded-md bg-[#3F2865] border border-[#FFD86B]/40 text-[11px] text-[#FFD86B] font-medium" data-testid="western-rising">
          ↑ Rising · {bt.rising_sign}
        </span>
      </div>

      {/* Wheel */}
      <div className="flex justify-center bg-[#0A0518] rounded-lg p-2 mb-3 border border-[#5C3A8A]/40">
        <NatalWheel
          planets={w.planets}
          ascLon={w.asc_longitude || 0}
          mcLon={w.mc_longitude || 0}
          size={wheelSize}
        />
      </div>

      {/* Placements table */}
      <div className="bg-[#FAF4E8] rounded-md p-2 mb-3 border border-[#5C3A8A]/40">
        <p className="text-[10px] uppercase tracking-wide text-[#7C2D12] font-semibold mb-1.5">
          Placements
        </p>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-[#3F2D1B]">
          {PLANET_ORDER.map((name) => {
            const p = w.planets[name];
            if (!p) return null;
            return (
              <div key={name} className="flex items-center gap-1.5" data-testid={`western-placement-${name.toLowerCase().replace(/\s+/g, '-')}`}>
                <span className="font-medium w-[78px]">{name}{p.retrograde ? " R" : ""}:</span>
                <span>
                  {p.sign} {p.degree_in_sign.toFixed(1)}°
                  <span className="text-[10px] text-muted ml-1">({p.house}H)</span>
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Aspects */}
      {tightAspects.length > 0 && (
        <div className="bg-[#FAF4E8] rounded-md p-2 mb-3 border border-[#5C3A8A]/40">
          <p className="text-[10px] uppercase tracking-wide text-[#7C2D12] font-semibold mb-1.5">
            Major aspects (orb ≤ 4°)
          </p>
          <div className="flex flex-wrap gap-1">
            {tightAspects.map((a, i) => (
              <span
                key={i}
                className={`px-1.5 py-0.5 rounded text-[10px] border ${TONE_COLOR[a.tone] || "bg-gray-100 text-gray-800 border-gray-300"}`}
                title={`${TONE_LABEL[a.tone] || a.tone} · orb ${a.orb}°`}
                data-testid={`western-aspect-${i}`}
              >
                {a.p1} {a.aspect} {a.p2}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Element & modality balance */}
      <div className="bg-[#FAF4E8] rounded-md p-2 border border-[#5C3A8A]/40">
        <p className="text-[10px] uppercase tracking-wide text-[#7C2D12] font-semibold mb-1.5">
          Elemental & modal balance
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
          {["Fire", "Earth", "Air", "Water"].map((el) => (
            <Bar
              key={el}
              label={el}
              count={elem[el] || 0}
              max={maxElem}
              color={ELEMENT_COLOR[el]}
            />
          ))}
          {["Cardinal", "Fixed", "Mutable"].map((md) => (
            <Bar
              key={md}
              label={md}
              count={modal[md] || 0}
              max={maxModal}
              color={MODALITY_COLOR[md]}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default WesternChartMessage;
